import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
    PlayCircle, StopCircle, Clock, MapPin, ClipboardList, Pill, FileText,
    ShieldCheck, Star, DollarSign, Activity, Plus, Trash2, CheckCircle2,
    AlertTriangle, Camera, PenTool, History,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const OPS = {
    root: "care-ops-root",
    startVisitBtn: "care-ops-start-visit-btn",
    endVisitBtn: "care-ops-end-visit-btn",
    activeVisit: "care-ops-active-visit",
    visitNotesInput: "care-ops-visit-notes-input",
    visitVitalsBP: "care-ops-visit-vitals-bp",
    visitVitalsPulse: "care-ops-visit-vitals-pulse",
    visitVitalsTemp: "care-ops-visit-vitals-temp",
    visitRow: "care-ops-visit-row",
    approveVisitBtn: "care-ops-approve-visit-btn",
    tab: "care-ops-tab",
    addMedName: "care-ops-med-name",
    addMedDose: "care-ops-med-dose",
    addMedTime: "care-ops-med-time",
    addMedBtn: "care-ops-add-med-btn",
    logMedBtn: "care-ops-log-med-btn",
    carePlanGoalsInput: "care-ops-plan-goals-input",
    carePlanAdlsInput: "care-ops-plan-adls-input",
    carePlanSaveBtn: "care-ops-plan-save-btn",
    complianceType: "care-ops-compliance-type",
    complianceName: "care-ops-compliance-name",
    complianceExpires: "care-ops-compliance-expires",
    complianceAddBtn: "care-ops-compliance-add-btn",
    profileBioInput: "care-ops-profile-bio",
    profileRateInput: "care-ops-profile-rate",
    profileSaveBtn: "care-ops-profile-save",
};

const TABS = {
    caregiver: [
        { key: "visits", label: "Visits & Clock-in", icon: Clock },
        { key: "care-plan", label: "Care plan", icon: ClipboardList },
        { key: "meds", label: "Meds (eMAR)", icon: Pill },
        { key: "compliance", label: "Compliance", icon: ShieldCheck },
        { key: "profile", label: "Public profile", icon: Star },
        { key: "timesheet", label: "Timesheet", icon: DollarSign },
    ],
    family: [
        { key: "care-plan", label: "Care plan", icon: ClipboardList },
        { key: "meds", label: "Meds (eMAR)", icon: Pill },
        { key: "visits", label: "Visit log", icon: Clock },
        { key: "timesheet", label: "Timesheet", icon: DollarSign },
        { key: "browse", label: "Browse caregivers", icon: Star },
    ],
};

export default function CareOpsPanel({ role, careRequestId, matchedCaregiverId }) {
    const [tab, setTab] = useState(TABS[role][0].key);
    const tabs = TABS[role] || [];

    return (
        <div data-testid={OPS.root} className="mt-6">
            <div className="flex flex-wrap gap-2 mb-4 border-b border-stone-200 pb-3">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            data-testid={OPS.tab}
                            data-tab={t.key}
                            onClick={() => setTab(t.key)}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium font-dmsans transition-all ${
                                tab === t.key
                                    ? "bg-stone-900 text-white"
                                    : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "visits" && role === "caregiver" && (
                <VisitsCaregiver careRequestId={careRequestId} />
            )}
            {tab === "visits" && role === "family" && <VisitsFamily />}
            {tab === "care-plan" && careRequestId && (
                <CarePlanEditor careRequestId={careRequestId} role={role} />
            )}
            {tab === "care-plan" && !careRequestId && (
                <Empty msg="Select or create a care request to edit its care plan." />
            )}
            {tab === "meds" && careRequestId && (
                <MedsPanel careRequestId={careRequestId} role={role} />
            )}
            {tab === "meds" && !careRequestId && (
                <Empty msg="Meds are tied to a care request. Post one first." />
            )}
            {tab === "compliance" && <CompliancePanel />}
            {tab === "incidents" && <IncidentsPanel role={role} careRequestId={careRequestId} />}
            {tab === "profile" && role === "caregiver" && <ProfileEditor />}
            {tab === "timesheet" && <Timesheet role={role} />}
            {tab === "browse" && <BrowseCaregivers />}
        </div>
    );
}

function Empty({ msg }) {
    return <div className="p-8 text-sm text-stone-500 font-dmsans text-center">{msg}</div>;
}

// ---------- Visits ----------
function VisitsCaregiver({ careRequestId }) {
    const [active, setActive] = useState(null);
    const [visits, setVisits] = useState([]);
    const [notes, setNotes] = useState("");
    const [vitals, setVitals] = useState({ bp: "", pulse: "", temp: "" });

    async function load() {
        try {
            const [a, v] = await Promise.all([
                api.get("/ops/visits/active"),
                api.get(`/ops/visits${careRequestId ? `?care_request_id=${careRequestId}` : ""}`),
            ]);
            setActive(a.data);
            setVisits(v.data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, [careRequestId]);

    async function start() {
        if (!careRequestId) return toast.error("No matched care request to clock in for");
        let lat, lng;
        try {
            const pos = await new Promise((res, rej) =>
                navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 })
            );
            lat = pos.coords.latitude;
            lng = pos.coords.longitude;
        } catch { /* location denied; proceed without */ }
        try {
            await api.post("/ops/visits/start", { care_request_id: careRequestId, lat, lng });
            toast.success("Clocked in");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function end() {
        if (!active) return;
        let lat, lng;
        try {
            const pos = await new Promise((res, rej) =>
                navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 })
            );
            lat = pos.coords.latitude;
            lng = pos.coords.longitude;
        } catch { /* noop */ }
        try {
            await api.post(`/ops/visits/${active.id}/end`, {
                lat, lng, notes,
                vitals: {
                    bp: vitals.bp || undefined,
                    pulse: vitals.pulse || undefined,
                    temp: vitals.temp || undefined,
                },
            });
            toast.success("Clocked out");
            setNotes(""); setVitals({ bp: "", pulse: "", temp: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    return (
        <div className="space-y-4">
            {active ? (
                <Card data-testid={OPS.activeVisit} className="rounded-2xl border-emerald-200 bg-emerald-50">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2 text-emerald-800">
                            <Activity className="w-5 h-5 animate-pulse" />
                            <span className="font-outfit font-semibold text-lg">Visit in progress</span>
                        </div>
                        <div className="text-sm text-emerald-700 font-dmsans mb-4">
                            Started {new Date(active.started_at).toLocaleString()}
                            {active.started_lat && (
                                <span className="ml-2 inline-flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> GPS verified
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div>
                                <Label className="text-xs">BP</Label>
                                <Input data-testid={OPS.visitVitalsBP} value={vitals.bp} onChange={(e) => setVitals({ ...vitals, bp: e.target.value })} placeholder="120/80" className="h-10 rounded-lg bg-white" />
                            </div>
                            <div>
                                <Label className="text-xs">Pulse</Label>
                                <Input data-testid={OPS.visitVitalsPulse} value={vitals.pulse} onChange={(e) => setVitals({ ...vitals, pulse: e.target.value })} placeholder="72" className="h-10 rounded-lg bg-white" />
                            </div>
                            <div>
                                <Label className="text-xs">Temp</Label>
                                <Input data-testid={OPS.visitVitalsTemp} value={vitals.temp} onChange={(e) => setVitals({ ...vitals, temp: e.target.value })} placeholder="98.6" className="h-10 rounded-lg bg-white" />
                            </div>
                        </div>
                        <Textarea
                            data-testid={OPS.visitNotesInput}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Visit notes (observations, changes, family concerns)…"
                            className="rounded-xl bg-white min-h-[80px] mb-3"
                        />
                        <Button data-testid={OPS.endVisitBtn} onClick={end} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                            <StopCircle className="w-4 h-4 mr-2" /> Clock out
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    data-testid={OPS.startVisitBtn}
                    onClick={start}
                    disabled={!careRequestId}
                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-12 px-6"
                >
                    <PlayCircle className="w-5 h-5 mr-2" /> Clock in for care visit
                </Button>
            )}

            <div className="space-y-2">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Recent visits</div>
                {visits.length === 0 && <p className="text-sm text-stone-500 font-dmsans">No visits yet.</p>}
                {visits.map((v) => (
                    <VisitRow key={v.id} visit={v} />
                ))}
            </div>
        </div>
    );
}

function VisitsFamily() {
    const [visits, setVisits] = useState([]);
    async function load() {
        try { const { data } = await api.get("/ops/visits"); setVisits(data); } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function approve(id) {
        try { await api.post(`/ops/visits/${id}/approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    return (
        <div className="space-y-2">
            {visits.length === 0 && <p className="text-sm text-stone-500 font-dmsans">No visits logged yet.</p>}
            {visits.map((v) => (
                <VisitRow
                    key={v.id}
                    visit={v}
                    action={v.status === "completed" && !v.approved ? (
                        <Button
                            data-testid={OPS.approveVisitBtn}
                            size="sm"
                            onClick={() => approve(v.id)}
                            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs"
                        >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                    ) : null}
                />
            ))}
        </div>
    );
}

function VisitRow({ visit, action }) {
    const [showPhotos, setShowPhotos] = useState(false);
    const geoBad = visit.started_geofence_ok === false || visit.ended_geofence_ok === false;
    return (
        <div data-testid={OPS.visitRow} className="p-4 border border-stone-200 rounded-xl">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-stone-900 text-sm">
                            {new Date(visit.started_at).toLocaleString()}
                        </span>
                        <Badge className={
                            visit.status === "in_progress" ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50" :
                            visit.approved ? "bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-50" :
                            "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-100"
                        }>
                            {visit.status === "in_progress" ? "In progress" : visit.approved ? "Approved" : visit.status}
                        </Badge>
                        {geoBad && (
                            <Badge className="bg-red-50 text-red-700 border-red-100 hover:bg-red-50 text-xs">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Geofence flag
                            </Badge>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-stone-500 font-dmsans">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {visit.duration_minutes || 0} min</span>
                        {(visit.started_lat || visit.ended_lat) && (
                            <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {geoBad ? `${visit.started_geofence_distance_m || visit.ended_geofence_distance_m}m off-site` : "GPS verified"}
                            </span>
                        )}
                        {visit.vitals?.bp && <span>BP {visit.vitals.bp}</span>}
                        {visit.vitals?.pulse && <span>Pulse {visit.vitals.pulse}</span>}
                        <button
                            data-testid="care-ops-toggle-photos"
                            onClick={() => setShowPhotos(!showPhotos)}
                            className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                        >
                            <Camera className="w-3 h-3" /> {showPhotos ? "Hide" : "Photos"}
                        </button>
                    </div>
                    {visit.notes && <div className="mt-2 text-xs text-stone-600 font-dmsans line-clamp-2">{visit.notes}</div>}
                    {showPhotos && <VisitPhotos visitId={visit.id} />}
                </div>
                {action}
            </div>
        </div>
    );
}

function VisitPhotos({ visitId }) {
    const { user } = useAuth();
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const inputRef = React.useRef(null);

    async function load() {
        try { const { data } = await api.get(`/evv/visits/${visitId}/photos`); setPhotos(data); }
        catch { /* noop */ }
    }
    useEffect(() => { load(); }, [visitId]);

    async function upload(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("caption", "");
            await api.post(`/evv/visits/${visitId}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Photo attached");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
    }

    return (
        <div className="mt-3 p-3 rounded-xl bg-stone-50 border border-stone-100">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Visit photos</div>
                {user?.role === "caregiver" && (
                    <>
                        <input
                            data-testid="care-ops-photo-upload"
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            onChange={upload}
                            disabled={uploading}
                            className="hidden"
                        />
                        <Button
                            data-testid="care-ops-photo-btn"
                            size="sm"
                            variant="outline"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            className="h-7 text-xs rounded-full"
                        >
                            <Camera className="w-3 h-3 mr-1" /> {uploading ? "..." : "Add photo"}
                        </Button>
                    </>
                )}
            </div>
            {photos.length === 0 ? (
                <div className="text-xs text-stone-500 font-dmsans">No photos yet.</div>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {photos.map((p) => (
                        <div key={p.id} className="text-xs bg-white border border-stone-200 rounded-lg px-2 py-1 font-dmsans">
                            {p.filename}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------- Care plan ----------
function CarePlanEditor({ careRequestId, role }) {
    const [plan, setPlan] = useState({ id: null, title: "Plan of Care", goals: [], adls: [], tasks: [] });
    const [goalsText, setGoalsText] = useState("");
    const [adlsText, setAdlsText] = useState("");
    const [versions, setVersions] = useState([]);
    const [signing, setSigning] = useState(false);

    async function load() {
        try {
            const { data } = await api.get(`/ops/care-plans/by-request/${careRequestId}`);
            setPlan({
                id: data.id || null,
                title: data.title || "Plan of Care",
                goals: data.goals || [],
                adls: data.adls || [],
                tasks: data.tasks || [],
            });
            setGoalsText((data.goals || []).join("\n"));
            setAdlsText((data.adls || []).join("\n"));
            if (data.id) {
                const v = await api.get(`/evv/care-plans/${data.id}/versions`);
                setVersions(v.data);
            } else {
                setVersions([]);
            }
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, [careRequestId]);

    async function save() {
        try {
            await api.post("/ops/care-plans", {
                care_request_id: careRequestId,
                title: plan.title,
                goals: goalsText.split("\n").filter(Boolean),
                adls: adlsText.split("\n").filter(Boolean),
                tasks: plan.tasks,
            });
            toast.success("Care plan saved");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function snapshot() {
        if (!plan.id) return toast.error("Save the plan first");
        try {
            await api.post(`/evv/care-plans/${plan.id}/version`);
            toast.success("Version snapshotted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function sign(versionId) {
        if (signing) return;
        const sig = window.prompt("Type your full name to sign this version:");
        if (!sig || sig.length < 2) return;
        setSigning(true);
        try {
            await api.post(`/evv/care-plans/versions/${versionId}/sign`, {
                care_plan_id: plan.id,
                signature_text: sig,
            });
            toast.success("Signed");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSigning(false); }
    }

    return (
        <div className="space-y-4">
            <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-6 space-y-4">
                    <div>
                        <Label>Title</Label>
                        <Input value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
                    </div>
                    <div>
                        <Label>Goals of care <span className="text-xs text-stone-400">(one per line)</span></Label>
                        <Textarea
                            data-testid={OPS.carePlanGoalsInput}
                            value={goalsText}
                            onChange={(e) => setGoalsText(e.target.value)}
                            placeholder="Maintain independence with ambulation&#10;Manage diabetes and monitor glucose"
                            className="mt-1.5 rounded-xl min-h-[100px]"
                        />
                    </div>
                    <div>
                        <Label>ADLs (Activities of Daily Living) <span className="text-xs text-stone-400">(one per line)</span></Label>
                        <Textarea
                            data-testid={OPS.carePlanAdlsInput}
                            value={adlsText}
                            onChange={(e) => setAdlsText(e.target.value)}
                            placeholder="Bathing assistance&#10;Dressing&#10;Meal prep"
                            className="mt-1.5 rounded-xl min-h-[100px]"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        {plan.id && (
                            <Button
                                data-testid="care-ops-snapshot-btn"
                                onClick={snapshot}
                                variant="outline"
                                className="rounded-full h-11"
                            >
                                <History className="w-4 h-4 mr-1.5" /> Snapshot version
                            </Button>
                        )}
                        <Button data-testid={OPS.carePlanSaveBtn} onClick={save} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6">
                            Save plan
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {versions.length > 0 && (
                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-6">
                        <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
                            <History className="w-5 h-5 text-emerald-700" /> Version history
                        </h3>
                        <div className="space-y-2">
                            {versions.map((v) => {
                                const locked = v.family_signature && v.caregiver_signature;
                                const mySig = role === "family" ? v.family_signature : v.caregiver_signature;
                                return (
                                    <div key={v.id} data-testid="care-ops-plan-version" className="p-3 border border-stone-200 rounded-xl">
                                        <div className="flex items-center justify-between gap-2">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-stone-900 text-sm">Version {v.version_number}</span>
                                                    {locked ? (
                                                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50 text-xs">
                                                            <CheckCircle2 className="w-3 h-3 mr-1" /> Locked
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-50 text-xs">
                                                            Awaiting signatures
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-xs text-stone-500 font-dmsans mt-0.5">
                                                    {new Date(v.created_at).toLocaleString()} · {(v.goals || []).length} goals · {(v.adls || []).length} ADLs
                                                </div>
                                                <div className="text-xs text-stone-600 font-dmsans mt-1">
                                                    Family: {v.family_signature ? `✓ ${v.family_signature}` : "unsigned"}
                                                    &nbsp;·&nbsp;
                                                    Caregiver: {v.caregiver_signature ? `✓ ${v.caregiver_signature}` : "unsigned"}
                                                </div>
                                            </div>
                                            {!mySig && (role === "family" || role === "caregiver") && (
                                                <Button
                                                    data-testid={`care-ops-sign-btn-${v.id}`}
                                                    onClick={() => sign(v.id)}
                                                    size="sm"
                                                    className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-8 text-xs shrink-0"
                                                >
                                                    <PenTool className="w-3 h-3 mr-1" /> Sign as {role}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// ---------- Medications ----------
function MedsPanel({ careRequestId, role }) {
    const [meds, setMeds] = useState([]);
    const [logs, setLogs] = useState([]);
    const [form, setForm] = useState({ name: "", dose: "", schedule: "" });

    async function load() {
        try {
            const [m, l] = await Promise.all([
                api.get(`/ops/medications?care_request_id=${careRequestId}`),
                api.get(`/ops/med-logs?care_request_id=${careRequestId}`),
            ]);
            setMeds(m.data); setLogs(l.data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, [careRequestId]);

    async function add(e) {
        e.preventDefault();
        try {
            await api.post("/ops/medications", {
                care_request_id: careRequestId,
                name: form.name, dose: form.dose,
                schedule: form.schedule.split(",").map((s) => s.trim()).filter(Boolean),
            });
            setForm({ name: "", dose: "", schedule: "" });
            toast.success("Medication added");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function logDose(med) {
        try {
            await api.post("/ops/med-logs", { medication_id: med.id, dose_given: med.dose });
            toast.success(`${med.name} administration logged`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    async function remove(id) {
        if (!window.confirm("Deactivate this medication?")) return;
        try { await api.delete(`/ops/medications/${id}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    return (
        <div className="space-y-4">
            <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-5">
                    <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <Input data-testid={OPS.addMedName} placeholder="Medication name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 rounded-xl" />
                        <Input data-testid={OPS.addMedDose} placeholder="Dose (e.g. 500mg)" value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })} required className="h-11 rounded-xl" />
                        <Input data-testid={OPS.addMedTime} placeholder="Times (08:00, 20:00)" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className="h-11 rounded-xl" />
                        <Button data-testid={OPS.addMedBtn} type="submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-11">
                            <Plus className="w-4 h-4 mr-1" /> Add med
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <div className="space-y-2">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Active medications</div>
                {meds.length === 0 && <p className="text-sm text-stone-500 font-dmsans">None yet.</p>}
                {meds.map((m) => (
                    <div key={m.id} className="p-4 border border-stone-200 rounded-xl flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="font-medium text-stone-900">{m.name} · {m.dose}</div>
                            <div className="text-xs text-stone-500 font-dmsans">
                                Schedule: {(m.schedule || []).join(", ") || "as needed"}
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            {role === "caregiver" && (
                                <Button data-testid={OPS.logMedBtn} size="sm" onClick={() => logDose(m)} className="rounded-full bg-sky-700 hover:bg-sky-800 text-white h-8 text-xs">
                                    Log dose
                                </Button>
                            )}
                            <Button size="icon" variant="ghost" onClick={() => remove(m.id)} className="h-8 w-8 rounded-full text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Administration log (eMAR)</div>
                {logs.length === 0 && <p className="text-sm text-stone-500 font-dmsans">No doses logged yet.</p>}
                {logs.map((l) => {
                    const m = meds.find((x) => x.id === l.medication_id);
                    return (
                        <div key={l.id} className="p-3 border border-stone-200 rounded-xl text-sm">
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-stone-900">{m?.name || "—"} · {l.dose_given}</span>
                                <span className="text-xs text-stone-400 font-dmsans">{new Date(l.administered_at).toLocaleString()}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Compliance ----------
function CompliancePanel() {
    const [docs, setDocs] = useState([]);
    const [expiring, setExpiring] = useState([]);
    const [form, setForm] = useState({ type: "license", name: "", expires_at: "" });

    async function load() {
        try {
            const [d, e] = await Promise.all([
                api.get("/compliance/docs"),
                api.get("/compliance/expiring?days=60"),
            ]);
            setDocs(d.data); setExpiring(e.data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function add(e) {
        e.preventDefault();
        try {
            await api.post("/compliance/docs", {
                type: form.type,
                name: form.name,
                expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
            });
            setForm({ type: "license", name: "", expires_at: "" });
            toast.success("Added");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    }

    async function remove(id) {
        try { await api.delete(`/compliance/docs/${id}`); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    }

    return (
        <div className="space-y-4">
            {expiring.length > 0 && (
                <Card className="rounded-2xl border-orange-200 bg-orange-50">
                    <CardContent className="p-4">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-700 mb-2">Expiring within 60 days</div>
                        {expiring.map((e) => (
                            <div key={e.id} className="text-sm text-orange-800 font-dmsans">
                                • {e.name} ({e.type}) — expires {new Date(e.expires_at).toLocaleDateString()}
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-5">
                    <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <select
                            data-testid={OPS.complianceType}
                            value={form.type}
                            onChange={(e) => setForm({ ...form, type: e.target.value })}
                            className="h-11 rounded-xl border border-stone-200 px-3 bg-white text-sm font-dmsans"
                        >
                            <option value="license">License</option>
                            <option value="tb_test">TB test</option>
                            <option value="flu_shot">Flu shot</option>
                            <option value="cpr">CPR cert</option>
                            <option value="background_check">Background check</option>
                            <option value="insurance">Insurance</option>
                            <option value="other">Other</option>
                        </select>
                        <Input data-testid={OPS.complianceName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Document name" required className="h-11 rounded-xl" />
                        <Input data-testid={OPS.complianceExpires} type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="h-11 rounded-xl" />
                        <Button data-testid={OPS.complianceAddBtn} type="submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-11">Add</Button>
                    </form>
                </CardContent>
            </Card>

            <div className="space-y-2">
                {docs.length === 0 && <p className="text-sm text-stone-500 font-dmsans">No compliance docs on file yet.</p>}
                {docs.map((d) => (
                    <div key={d.id} className="p-3 border border-stone-200 rounded-xl flex items-center justify-between">
                        <div>
                            <span className="font-medium text-stone-900 text-sm">{d.name}</span>
                            <span className="text-xs text-stone-500 ml-2 font-dmsans">{d.type}</span>
                            {d.expires_at && (
                                <div className="text-xs text-stone-500 font-dmsans">Expires {new Date(d.expires_at).toLocaleDateString()}</div>
                            )}
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => remove(d.id)} className="h-8 w-8 rounded-full text-red-600 hover:bg-red-50">
                            <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- Profile ----------
function ProfileEditor() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({
        bio: user?.bio || "",
        hourly_rate_usd: user?.hourly_rate_cents ? user.hourly_rate_cents / 100 : "",
        on_call: user?.on_call || false,
        emergency_contact_name: user?.emergency_contact_name || "",
        emergency_contact_phone: user?.emergency_contact_phone || "",
    });

    async function save() {
        try {
            await api.put("/social/profile", {
                bio: form.bio,
                hourly_rate_cents: form.hourly_rate_usd ? Math.round(parseFloat(form.hourly_rate_usd) * 100) : null,
                on_call: form.on_call,
                emergency_contact_name: form.emergency_contact_name || null,
                emergency_contact_phone: form.emergency_contact_phone || null,
            });
            toast.success("Profile updated");
            await refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6 space-y-4">
                <div>
                    <Label>Bio (visible to families)</Label>
                    <Textarea data-testid={OPS.profileBioInput} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell families about your experience, style of care, and what makes you great." className="mt-1.5 rounded-xl min-h-[120px]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Hourly rate (USD)</Label>
                        <Input data-testid={OPS.profileRateInput} type="number" step="0.01" value={form.hourly_rate_usd} onChange={(e) => setForm({ ...form, hourly_rate_usd: e.target.value })} className="mt-1.5 h-11 rounded-xl" placeholder="28.00" />
                    </div>
                    <div className="flex items-end gap-2">
                        <label className="flex items-center gap-2 h-11 font-dmsans text-sm">
                            <input type="checkbox" checked={form.on_call} onChange={(e) => setForm({ ...form, on_call: e.target.checked })} className="w-4 h-4 rounded" />
                            Available for on-call / emergency routing
                        </label>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Emergency contact name</Label>
                        <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
                    </div>
                    <div>
                        <Label>Emergency contact phone</Label>
                        <Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className="mt-1.5 h-11 rounded-xl" placeholder="+1…" />
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button data-testid={OPS.profileSaveBtn} onClick={save} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6">Save profile</Button>
                </div>
            </CardContent>
        </Card>
    );
}

// ---------- Timesheet ----------
function Timesheet({ role }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try { const r = await api.get("/ops/timesheets/summary"); setData(r.data); } catch { /* noop */ }
        })();
    }, []);
    if (!data) return <div className="text-sm text-stone-500 font-dmsans">Loading…</div>;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
                <StatBox label="Total hours" value={data.total_hours} />
                <StatBox label="Approved" value={data.approved_hours} accent="emerald" />
                <StatBox label="Pending approval" value={data.unapproved_hours} accent="orange" />
            </div>
            <div className="space-y-2">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Recent completed visits</div>
                {data.visits.map((v) => (
                    <div key={v.id} className="p-3 border border-stone-200 rounded-xl flex items-center justify-between text-sm">
                        <span>{new Date(v.started_at).toLocaleString()}</span>
                        <span className="text-stone-500 font-dmsans">{v.duration_minutes || 0} min · {v.approved ? "Approved" : "Pending"}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatBox({ label, value, accent = "stone" }) {
    const map = { stone: "text-stone-900", emerald: "text-emerald-700", orange: "text-orange-700" };
    return (
        <div className="p-4 rounded-xl border border-stone-200 bg-white">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">{label}</div>
            <div className={`font-outfit text-2xl font-semibold mt-1 ${map[accent]}`}>{value}</div>
        </div>
    );
}

// ---------- Browse caregivers (family view) ----------
function BrowseCaregivers() {
    const [caregivers, setCaregivers] = useState([]);
    const [credentialFilter, setCredentialFilter] = useState("");
    async function load() {
        try {
            const q = credentialFilter ? `?credential=${credentialFilter}` : "";
            const { data } = await api.get(`/social/caregivers${q}`);
            setCaregivers(data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, [credentialFilter]);
    return (
        <div className="space-y-4">
            <div className="flex gap-2">
                {["", "RN", "LPN", "CNA", "HHA"].map((c) => (
                    <button
                        key={c || "ALL"}
                        onClick={() => setCredentialFilter(c)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium ${
                            credentialFilter === c ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"
                        }`}
                    >
                        {c || "All"}
                    </button>
                ))}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {caregivers.length === 0 && <p className="text-sm text-stone-500 font-dmsans">No caregivers yet.</p>}
                {caregivers.map((c) => (
                    <div key={c.id} className="p-4 border border-stone-200 rounded-2xl hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center font-outfit font-bold text-emerald-800">
                                {(c.full_name || "?")[0]}
                            </div>
                            <div>
                                <div className="font-medium text-stone-900">{c.full_name}</div>
                                <div className="text-xs text-emerald-700 font-bold uppercase tracking-[0.2em]">{c.credential}</div>
                            </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs text-stone-500 font-dmsans">
                            <Star className="w-3.5 h-3.5 text-orange-500 fill-orange-500" />
                            {c.avg_rating ? `${c.avg_rating} (${c.rating_count})` : "New"}
                            {c.hourly_rate_cents && <span>· ${(c.hourly_rate_cents / 100).toFixed(0)}/hr</span>}
                        </div>
                        {c.bio && <p className="text-xs text-stone-600 mt-2 line-clamp-3 font-dmsans">{c.bio}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- Incidents ----------
function IncidentsPanel({ role, careRequestId }) {
    const [incidents, setIncidents] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        kind: "fall", severity: "medium", description: "",
        family_notified: false, reported_to_911: false,
    });

    async function load() {
        try { const { data } = await api.get("/evv/incidents"); setIncidents(data); }
        catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function submit(e) {
        e.preventDefault();
        try {
            await api.post("/evv/incidents", {
                care_request_id: careRequestId || undefined,
                kind: form.kind, severity: form.severity,
                description: form.description,
                family_notified: form.family_notified,
                reported_to_911: form.reported_to_911,
            });
            toast.success("Incident reported");
            setShowForm(false);
            setForm({ ...form, description: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    const sevColor = {
        low: "bg-stone-100 text-stone-700 border-stone-200",
        medium: "bg-orange-50 text-orange-700 border-orange-100",
        high: "bg-red-50 text-red-700 border-red-100",
        critical: "bg-red-100 text-red-800 border-red-200",
    };

    return (
        <div className="space-y-4">
            {role === "caregiver" && (
                <div className="flex justify-end">
                    <Button
                        data-testid="care-ops-incident-new-btn"
                        onClick={() => setShowForm(!showForm)}
                        className="rounded-full bg-orange-600 hover:bg-orange-700 text-white"
                    >
                        <AlertTriangle className="w-4 h-4 mr-2" /> {showForm ? "Cancel" : "Report incident"}
                    </Button>
                </div>
            )}
            {showForm && role === "caregiver" && (
                <Card className="rounded-2xl border-orange-200 bg-orange-50/40">
                    <CardContent className="p-6">
                        <form onSubmit={submit} className="space-y-3">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>Kind</Label>
                                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                                        <SelectTrigger data-testid="care-ops-incident-kind" className="h-11 rounded-xl mt-1.5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="fall">Fall</SelectItem>
                                            <SelectItem value="injury">Injury</SelectItem>
                                            <SelectItem value="med_error">Medication error</SelectItem>
                                            <SelectItem value="behavior">Behavioral</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Severity</Label>
                                    <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                                        <SelectTrigger data-testid="care-ops-incident-severity" className="h-11 rounded-xl mt-1.5">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                            <SelectItem value="critical">Critical</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea
                                    data-testid="care-ops-incident-desc"
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    required
                                    className="mt-1.5 rounded-xl min-h-[80px]"
                                    placeholder="What happened? Who was involved? Any injuries or follow-up needed?"
                                />
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm font-dmsans">
                                <label className="flex items-center gap-2">
                                    <input
                                        data-testid="care-ops-incident-fam"
                                        type="checkbox"
                                        checked={form.family_notified}
                                        onChange={(e) => setForm({ ...form, family_notified: e.target.checked })}
                                        className="w-4 h-4"
                                    />
                                    Family notified
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        data-testid="care-ops-incident-911"
                                        type="checkbox"
                                        checked={form.reported_to_911}
                                        onChange={(e) => setForm({ ...form, reported_to_911: e.target.checked })}
                                        className="w-4 h-4"
                                    />
                                    Emergency services called
                                </label>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    data-testid="care-ops-incident-submit"
                                    type="submit"
                                    className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-11 px-6"
                                >
                                    Submit report
                                </Button>
                            </div>
                            {(form.severity === "high" || form.severity === "critical") && (
                                <p className="text-xs text-orange-700 font-dmsans">
                                    {form.severity.toUpperCase()} severity will auto-email the family.
                                </p>
                            )}
                        </form>
                    </CardContent>
                </Card>
            )}
            {incidents.length === 0 && (
                <p className="text-sm text-stone-500 font-dmsans">No incidents on record.</p>
            )}
            {incidents.map((i) => (
                <div key={i.id} data-testid="care-ops-incident-row" className="p-4 border border-stone-200 rounded-xl">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-stone-900 text-sm capitalize">{i.kind.replace("_", " ")}</span>
                                <Badge className={`${sevColor[i.severity] || sevColor.low} text-xs hover:opacity-100`}>
                                    {i.severity}
                                </Badge>
                                {i.reported_to_911 && (
                                    <Badge className="bg-red-50 text-red-700 border-red-100 text-xs hover:bg-red-50">
                                        911 called
                                    </Badge>
                                )}
                            </div>
                            <div className="text-xs text-stone-500 font-dmsans mt-1">
                                {new Date(i.created_at).toLocaleString()} · reported by {i.reporter_role}
                            </div>
                            <p className="mt-2 text-sm text-stone-700 font-dmsans">{i.description}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
